import React from 'react';
import { LevelStatusItem } from '../../../../services/gamification/gamification.service';
import {
  lvlBeginner,
  lvlIntermediate,
  lvlAdvanced,
  lvlExpert,
  lvlExpertPlus,
  lvlMaster,
  greenCheck,
  lockIcon,
  groupUsersIcon
} from '../../../../assets/images/index';

interface LevelCardItemProps {
  level: LevelStatusItem;
  t: (key: string) => string;
}

const LEVEL_AVATARS: Record<string, string> = {
  lvl_beginner: lvlBeginner,
  lvl_intermediate: lvlIntermediate,
  lvl_advanced: lvlAdvanced,
  lvl_expert: lvlExpert,
  lvl_expert_plus: lvlExpertPlus,
  lvl_master: lvlMaster,

  // Key fallbacks
  beginner: lvlBeginner,
  apprentice: lvlIntermediate,
  intermediate: lvlIntermediate,
  skilled: lvlAdvanced,
  advanced: lvlAdvanced,
  star: lvlExpert,
  expert: lvlExpert,
  master: lvlMaster,
  legend: lvlExpertPlus
};

export const LevelCardItem: React.FC<LevelCardItemProps> = ({ level, t }) => {
  const avatarSrc = (level.icon && LEVEL_AVATARS[level.icon]) || LEVEL_AVATARS[level.key] || lvlBeginner;

  const cardModifier = level.state === 'completed'
    ? 'v2-level-card-item--completed'
    : level.state === 'active'
      ? 'v2-level-card-item--active'
      : 'v2-level-card-item--locked';

  const neededText = level.state === 'active'
    ? t('pointsNeededToAdvance')
        .replace('{{count}}', (level.points_needed_to_next || 0).toLocaleString())
        .replace('{{target}}', level.target_level_label || '')
    : level.state === 'locked'
      ? t('pointsNeededToComplete').replace('{{count}}', (level.points_needed || 0).toLocaleString())
      : null;

  const targetPointsFormatted = t('targetLevelPoints').replace('{{points}}', (level.max_points || 0).toLocaleString());

  return (
    <div className={`v2-level-card-item ${cardModifier}`}>
      {/* Placement Pills Header - Positioned at top right sitting on top border */}
      {(level.is_current_level || level.is_class_level) && (
        <div className="v2-level-card-item__pills-header">
          {level.is_current_level && (
            <span className="v2-level-pill v2-level-pill--user">
              {t('youAreHere')}
            </span>
          )}
          {level.is_class_level && (
            <span className="v2-level-pill v2-level-pill--group">
              <img src={groupUsersIcon} alt="" className="v2-level-pill__icon" />
              {t('classIsHere')}
            </span>
          )}
        </div>
      )}

      <div className="v2-level-card-item__main">
        {/* 1. Level Illustration / Avatar (Rightmost in RTL) */}
        <div className="v2-level-card-item__avatar-wrapper">
          <img src={avatarSrc} alt={level.label} className="v2-level-card-item__avatar" />
        </div>

        {/* 2. Main Text Content (Middle in RTL) */}
        <div className="v2-level-card-item__content">
          <div className="v2-level-card-item__title">{level.label}</div>
          <div className="v2-level-card-item__target-points">
            {targetPointsFormatted}
          </div>
        </div>

        {/* 3. State Chip (Leftmost in RTL) */}
        <div className="v2-level-card-item__chip-container">
          {level.state === 'completed' && (
            <div className="v2-level-chip v2-level-chip--completed">
              <span className="v2-level-chip__label">{t('completedLevel')}</span>
              <img src={greenCheck} alt="" className="v2-level-chip__icon" />
            </div>
          )}

          {level.state === 'active' && (
            <div className="v2-level-chip v2-level-chip--active">
              <span className="v2-level-chip__label">{level.progress_percentage}%</span>
            </div>
          )}

          {level.state === 'locked' && (
            <div className="v2-level-chip v2-level-chip--locked">
              <img src={lockIcon} alt="" className="v2-level-chip__icon" />
              <span className="v2-level-chip__label">{t('lockedLevel')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Peach Highlight Box for Remaining Points */}
      {neededText && (
        <div className="v2-level-card-item__needed-box">
          {neededText}
        </div>
      )}
    </div>
  );
};
